/**
 * syncDbHelpers.ts — Helpers para persistência de dados de sincronização.
 * Responsabilidade: upsert de contatos, conversas e re-upload de mídias.
 * 
 * Usa .upsert() para conversas para eliminar erros 409 (Conflict) no console.
 */
import { supabase } from '../../../lib/supabase';
import { getMimeType } from './syncSingleChat';
import { isTemporaryRemoteMediaUrl } from '../../../utils/failedImageCache';
import { contactPhoneKeyVariants, isUsableContactName } from '../../../utils/phoneUtils';

function buildContactPhoneFilter(phone: string): string {
    return contactPhoneKeyVariants(phone)
        .flatMap((variant) => [`phone.eq.${variant}`, `whatsapp.eq.${variant}`])
        .join(',');
}

async function findExistingContactId(
    organizationId: string,
    contactPhone: string,
): Promise<string | null> {
    const { data } = await supabase
        .from('contacts')
        .select('id')
        .eq('organization_id', organizationId)
        .or(buildContactPhoneFilter(contactPhone))
        .limit(1)
        .maybeSingle();

    return data?.id || null;
}

export const upsertContact = async (
    contactPhone: string,
    contactName: string,
    organizationId: string,
    imagePreview: string | null
): Promise<string | null> => {
    if (!contactPhone) return null;

    // 1. Tenta achar contato existente PRIMEIRO para evitar criar duplicatas (a ON CONFLICT às vezes falha por diferenças no "whatsapp" VS "phone")
    let existingId = await findExistingContactId(organizationId, contactPhone);

    if (existingId) {
        const updates: Record<string, unknown> = {
            last_interaction_at: new Date().toISOString(),
        };

        if (isUsableContactName(contactName, contactPhone)) {
            const { data: current } = await supabase
                .from('contacts')
                .select('name')
                .eq('id', existingId)
                .maybeSingle();

            if (!isUsableContactName(current?.name, contactPhone)) {
                updates.name = contactName;
            }
        }

        await supabase
            .from('contacts')
            .update(updates)
            .eq('id', existingId);

        if (imagePreview && !isTemporaryRemoteMediaUrl(imagePreview)) {
            await supabase
                .from('contacts')
                .update({ profile_picture_url: imagePreview })
                .eq('id', existingId)
                .is('profile_picture_url', null);
        }
        return existingId;
    }

    // 2. Não existia, então inserimos de fato um novo.
    const persistedAvatar = imagePreview && !isTemporaryRemoteMediaUrl(imagePreview) ? imagePreview : null;

    const { data: newContact, error: insertError } = await supabase
        .from('contacts')
        .insert({
            organization_id: organizationId,
            name: contactName,
            whatsapp: contactPhone,
            phone: contactPhone,
            profile_picture_url: persistedAvatar,
            status: 'active',
            last_interaction_at: new Date().toISOString()
        })
        .select('id')
        .single();

    if (!insertError && newContact) {
        return newContact.id;
    }

    // 3. Fallback: se insert falhou (race condition), tenta buscar novamente
    return findExistingContactId(organizationId, contactPhone);
};

export const syncConversation = async (
    connectionId: string,
    remoteJid: string,
    contactId: string | null,
    contactName: string,
    contactPhone: string,
    organizationId: string,
    integrationUserId: string | undefined,
    rawChat: any,
    chat: any
): Promise<{ conversationDbId: string | undefined; hasError: boolean }> => {
    const chatLastMsg = rawChat.wa_lastMessageTextVote || rawChat.wa_lastMessageBody || rawChat.wa_lastMessageText || rawChat.lastMsg || rawChat.lastMessage || null;
    const chatLastTs = rawChat.wa_lastMsgTimestamp || rawChat.lastMsgTimestamp || rawChat.wa_t || rawChat.timestamp || null;
    const lastMsgTime = chatLastTs ? (chatLastTs > 9999999999 ? new Date(chatLastTs).toISOString() : new Date(chatLastTs * 1000).toISOString()) : new Date().toISOString();

    // RPC ATÔMICA — Faz INSERT-or-SELECT dentro do Postgres.
    // Nunca produz erro HTTP 409 porque constraint violations são tratadas
    // internamente com EXCEPTION WHEN unique_violation.
    const { data: convId, error: rpcError } = await supabase.rpc('upsert_chat_conversation', {
        p_connection_id: connectionId,
        p_remote_jid: remoteJid,
        p_contact_id: contactId,
        p_contact_name: contactName,
        p_contact_phone: contactPhone,
        p_avatar_url: (() => {
            const raw = rawChat.imagePreview || chat.imagePreview || chat.image || null;
            return raw && !isTemporaryRemoteMediaUrl(String(raw)) ? raw : null;
        })(),
        p_status: 'open',
        p_organization_id: organizationId,
        p_user_id: integrationUserId || null,
        p_unread_count: rawChat.wa_unreadCount || 0,
        p_last_message: chatLastMsg,
        p_last_message_time: lastMsgTime,
        p_updated_at: lastMsgTime
    });

    if (rpcError || !convId) {
        console.warn(`[History Sync] RPC upsert_chat_conversation falhou para ${remoteJid}:`, rpcError?.message);
        return { conversationDbId: undefined, hasError: true };
    }

    return { conversationDbId: convId as string, hasError: false };
};

/**
 * Re-upload de mídia para Cloudflare R2 via Edge Function media-reupload.
 * Processa todas as mensagens com fileURL preenchido (resolvido pelo sync.ts).
 */
export const reuploadMediaBatch = async (newMessages: any[]) => {
    const mediaMsgs = newMessages.filter(m => m.fileURL);
    if (!mediaMsgs.length) return;

    try {
        // Processa em lotes de 5 — evita competir com envio/exclusão na UAZAPI
        for (let i = 0; i < mediaMsgs.length; i += 5) {
            const batch = mediaMsgs.slice(i, i + 5).map(m => ({
                sourceUrl: m.fileURL!,
                messageId: m.messageid,
                mimeType: getMimeType(m.messageType)
            }));

            const { data: reuploadResult, error: reuploadError } = await supabase.functions.invoke('media-reupload', { body: { urls: batch } });

            if (!reuploadError && reuploadResult?.results) {
                for (const r of reuploadResult.results) {
                    if (r.publicUrl) {
                        await supabase
                            .from('chat_messages')
                            .update({ media_url: r.publicUrl })
                            .eq('message_id', r.messageId);
                    }
                }
                const uploaded = reuploadResult.results.filter((r: any) => r.publicUrl).length;
                if (uploaded > 0) {
                    console.log(`[History Sync] ${uploaded} mídias re-uploaded para R2 (lote ${Math.floor(i / 5) + 1})`);
                }
            }

            // Delay entre lotes
            if (i + 5 < mediaMsgs.length) {
                await new Promise(r => setTimeout(r, 300));
            }
        }
    } catch (mediaErr: any) {
        console.warn(`[History Sync] Erro re-upload mídia:`, mediaErr.message);
    }
};
